import React, { KeyboardEvent, MouseEvent, ReactNode, useState } from 'react';
import {
  Accordion,
  ActionItem,
  Avatar,
  Box,
  Button,
  Chip,
  ColumnDef,
  DataTable,
  Icons,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import formatDate from '@core/utils/formatDate';
import { HANDIN_PROVIDER } from '@core/assignments/constants';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import DeleteSubmissionModal from '../SubmissionActions/DeleteSubmissionModal';
import SubmissionActions from '../SubmissionActions';
import { createStyles } from './SubmissionAccordions.styles';
import {
  ResponsiveGroupedSubmissionGroups,
  ResponsiveSubmissionGroup,
  ResponsiveSubmissionGroups,
  ResponsiveSubmissionItem,
} from './SubmissionAccordions.types';
import { getSubmissionCapabilities } from './submissionCapabilities';

type Props = {
  accessingAsStaff: boolean;
  expandPopulatedSections?: boolean;
  groupedSubmissionGroups?: ResponsiveGroupedSubmissionGroups;
  hideNoSubmissionIdentities?: boolean;
  isEducator: boolean;
  isGroupAssignment?: boolean;
  onDeleteSubmission?: (
    submission: ResponsiveSubmissionItem
  ) => Promise<void> | void;
  onOpenSubmission?: (submission: ResponsiveSubmissionItem) => void;
  provider: string;
  providerTitle?: string;
  submissionGroups: ResponsiveSubmissionGroups;
  unassignedStudents?: ResponsiveSubmissionItem[];
};

type SectionKey = keyof ResponsiveSubmissionGroups;

type MobileGroupSubmissionProps = {
  canOpenGroup: boolean;
  children: ReactNode;
  group: ResponsiveSubmissionGroup;
  onOpenGroup: () => void;
  styles: ReturnType<typeof createStyles>;
};

const MobileGroupSubmission = ({
  canOpenGroup,
  children,
  group,
  onOpenGroup,
  styles,
}: MobileGroupSubmissionProps) => {
  const [expanded, setExpanded] = useState(true);
  const membersId = `group-submissions-${group.id || group.name}`.replace(
    /[^a-zA-Z0-9-_]/g,
    '-'
  );

  return (
    <Box component="section" sx={styles.mobileGroup}>
      <Chip label="Group submission" size="small" sx={styles.groupChip} />

      <Box sx={styles.mobileGroupInformation}>
        <Typography sx={styles.mobileGroupName}>{group.name}</Typography>
        <Typography sx={styles.mobileGroupDetail}>
          <Box component="span" sx={styles.mobileGroupDetailLabel}>
            Submission date:{' '}
          </Box>
          {group.receivedAt ? formatDate(group.receivedAt) : 'No submission'}
        </Typography>
        <Typography sx={styles.mobileGroupDetail}>
          <Box component="span" sx={styles.mobileGroupDetailLabel}>
            Grade:{' '}
          </Box>
          {getSubmissionGrade(group)}
        </Typography>
      </Box>

      <Box sx={styles.mobileGroupActions}>
        {canOpenGroup && (
          <Button
            kind="secondary"
            onClick={onOpenGroup}
            size="medium"
            sx={{
              ...styles.mobileGroupAction,
              ...styles.mobileGroupOpenAction,
            }}
            type="button"
          >
            Open in Handin
            <Icons.OpenInNew aria-hidden fontSize="small" />
          </Button>
        )}
        <Button
          aria-controls={membersId}
          aria-expanded={expanded}
          kind="secondary"
          onClick={() => setExpanded((isExpanded) => !isExpanded)}
          size="medium"
          sx={{
            ...styles.mobileGroupAction,
            ...styles.mobileGroupToggleAction,
          }}
          type="button"
        >
          {expanded ? 'Hide submissions' : 'View submissions'}
          {expanded ? (
            <Icons.KeyboardArrowUp aria-hidden fontSize="small" />
          ) : (
            <Icons.KeyboardArrowDown aria-hidden fontSize="small" />
          )}
        </Button>
      </Box>

      {expanded && (
        <Box id={membersId} sx={styles.mobileGroupMembers}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const StudentName = ({
  name,
  sx,
}: {
  name: string;
  sx: ReturnType<typeof createStyles>['name'];
}) => {
  const [ref, isOverflowing] = useIsOverflowing<HTMLSpanElement>();

  return (
    <Tooltip title={isOverflowing ? name : ''} placement="top" arrow>
      <Box component="span" ref={ref} sx={sx}>
        {name}
      </Box>
    </Tooltip>
  );
};

const sections: Array<{ key: SectionKey; label: string }> = [
  { key: 'earlySubmissions', label: 'On time submissions' },
  { key: 'lateSubmissions', label: 'Late submissions' },
  { key: 'noSubmissions', label: 'No submission' },
];

const capitaliseNamePart = (name?: string) => {
  const trimmedName = name?.trim();
  return trimmedName
    ? `${trimmedName.charAt(0).toUpperCase()}${trimmedName.slice(1)}`
    : '';
};

export const getSubmissionName = ({ user }: ResponsiveSubmissionItem) =>
  [capitaliseNamePart(user.firstName), capitaliseNamePart(user.lastName)]
    .filter(Boolean)
    .join(' ') || 'Unnamed student';

export const getSubmissionInitials = ({ user }: ResponsiveSubmissionItem) => {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((name) => name?.trim().charAt(0))
    .join('')
    .toUpperCase();

  return initials || 'U';
};

const getSubmissionGrade = (submission: {
  displayGrade?: string;
  receivedAt?: string;
}) => {
  if (submission.displayGrade) {
    return submission.displayGrade.includes('NaN')
      ? 'Not graded'
      : submission.displayGrade;
  }

  return submission.receivedAt ? 'Not graded' : '-';
};

const SubmissionAccordions = ({
  accessingAsStaff,
  expandPopulatedSections = false,
  groupedSubmissionGroups,
  hideNoSubmissionIdentities = false,
  isEducator,
  isGroupAssignment = false,
  onDeleteSubmission,
  onOpenSubmission,
  provider,
  providerTitle,
  submissionGroups,
  unassignedStudents = [],
}: Props) => {
  const theme = useTheme();
  const useMobileGroupPresentation = useMediaQuery(
    theme.breakpoints.down('sm')
  );
  const useTablePresentation = !useMobileGroupPresentation;
  const styles = createStyles(theme);
  const normalisedProvider = provider === 'handin' ? HANDIN_PROVIDER : provider;
  const groupActionProviderTitle =
    providerTitle ||
    (normalisedProvider === HANDIN_PROVIDER ? 'Handin' : normalisedProvider);
  const [submissionPendingDelete, setSubmissionPendingDelete] =
    useState<ResponsiveSubmissionItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getItemDetails = (submission: ResponsiveSubmissionItem) => {
    const hasSubmission = Boolean(submission.id);
    return {
      capabilities: getSubmissionCapabilities({
        accessingAsStaff,
        hasSubmission,
        isEducator,
        provider: normalisedProvider,
      }),
      date: submission.receivedAt
        ? formatDate(submission.receivedAt)
        : 'No submission',
      grade: getSubmissionGrade(submission),
      hasSubmission,
      name: getSubmissionName(submission),
    };
  };

  const openSubmission = (submission: ResponsiveSubmissionItem) => {
    if (getItemDetails(submission).capabilities.canOpen) {
      onOpenSubmission?.(submission);
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent,
    submission: ResponsiveSubmissionItem
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openSubmission(submission);
  };

  const handleDesktopNameClick = (
    event: MouseEvent,
    submission: ResponsiveSubmissionItem
  ) => {
    event.stopPropagation();
    openSubmission(submission);
  };

  const confirmDesktopDelete = async () => {
    if (!submissionPendingDelete || !onDeleteSubmission || deleting) return;
    setDeleting(true);
    try {
      await onDeleteSubmission(submissionPendingDelete);
      setSubmissionPendingDelete(null);
    } catch (_error) {
      // The existing delete operation displays the API error toast. Keep the
      // confirmation open so the educator can retry or cancel.
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnDef<ResponsiveSubmissionItem>[] = [
    {
      field: 'user',
      headerName: 'Name',
      width: '50.5%',
      renderCell: (submission) => {
        const name = getSubmissionName(submission);
        const identity = (
          <>
            <Avatar
              altText={name}
              fallbackText={getSubmissionInitials(submission)}
              src={submission.user.avatar}
              sx={styles.avatar}
            />
            <StudentName name={name} sx={styles.name} />
          </>
        );
        return getItemDetails(submission).capabilities.canOpen ? (
          <Button
            aria-label={`Open ${name} submission`}
            kind="secondary"
            onClick={(event) => handleDesktopNameClick(event, submission)}
            sx={{ ...styles.studentIdentity, ...styles.tableNameButton }}
            type="button"
          >
            {identity}
          </Button>
        ) : (
          <Box sx={styles.studentIdentity}>{identity}</Box>
        );
      },
    },
    {
      field: 'receivedAt',
      headerName: 'Submission date',
      width: '30.3%',
      renderCell: (submission) => (
        <Typography sx={styles.rowValue}>
          {getItemDetails(submission).date}
        </Typography>
      ),
    },
    {
      field: 'displayGrade',
      headerName: 'Grade',
      width: '19.2%',
      renderCell: (submission) => (
        <Typography sx={styles.rowValue}>
          {getItemDetails(submission).grade}
        </Typography>
      ),
    },
  ];

  const deleteActions: ActionItem<ResponsiveSubmissionItem>[] = [
    {
      icon: <Icons.DeleteOutlined fontSize="small" />,
      label: 'Delete submission',
      onClick: setSubmissionPendingDelete,
    },
  ];

  const renderTable = (
    submissions: ResponsiveSubmissionItem[],
    allowHandinRowOpen = false
  ) => {
    const canOpenRows = submissions.some((submission) => {
      const { canOpen, canOpenRow } = getItemDetails(submission).capabilities;
      return canOpenRow || (allowHandinRowOpen && canOpen);
    });
    const canDeleteRows = submissions.some(
      (submission) => getItemDetails(submission).capabilities.canDelete
    );

    return (
      <DataTable
        actionCellStyle={{ width: 56 }}
        actions={deleteActions}
        clickableRows={canOpenRows}
        columnStyles={styles.tableCell}
        columns={columns}
        containerStyle={{
          ...styles.table,
          ...(canOpenRows ? {} : styles.staticTable),
        }}
        getRowId={(submission) => submission.id || submission.user.id}
        headerStyle={styles.tableHeader}
        onRowClick={openSubmission}
        rows={submissions}
        showActions={canDeleteRows}
      />
    );
  };

  const renderCards = (
    submissions: ResponsiveSubmissionItem[],
    dateLabel = 'Submission date',
    allowHandinCardOpen = false
  ) => (
    <Box sx={styles.cards}>
      {submissions.map((submission) => {
        const { capabilities, date, grade, hasSubmission, name } =
          getItemDetails(submission);
        const canOpenCard =
          capabilities.canOpenRow ||
          (allowHandinCardOpen && capabilities.canOpen);
        const cardName =
          capabilities.canOpen && !canOpenCard ? (
            <Button
              aria-label={`Open ${name} submission`}
              kind="secondary"
              onClick={(event) => handleDesktopNameClick(event, submission)}
              sx={styles.cardNameButton}
              type="button"
            >
              {name}
            </Button>
          ) : (
            <Typography sx={styles.cardName}>{name}</Typography>
          );

        return (
          <Box
            aria-label={`${name} submission`}
            component="article"
            key={submission.id || submission.user.id}
            onClick={canOpenCard ? () => openSubmission(submission) : undefined}
            onKeyDown={
              canOpenCard
                ? (event) => handleKeyDown(event, submission)
                : undefined
            }
            role={canOpenCard ? 'button' : undefined}
            sx={{
              ...styles.card,
              ...(canOpenCard ? styles.clickableCard : {}),
            }}
            tabIndex={canOpenCard ? 0 : undefined}
          >
            <Box sx={styles.cardHeader}>
              {cardName}
              <SubmissionActions
                canDeleteSubmission={capabilities.canDelete}
                hasSubmission={hasSubmission}
                onDeleteSubmission={
                  capabilities.canDelete && onDeleteSubmission
                    ? () => onDeleteSubmission(submission)
                    : undefined
                }
                provider={normalisedProvider}
                submissionId={submission.id}
                studentName={name}
              />
            </Box>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                {dateLabel}:{' '}
              </Box>
              {date}
            </Typography>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                Grade:{' '}
              </Box>
              {grade}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderSectionContent = (
    submissions: ResponsiveSubmissionItem[],
    hideIdentities = false,
    allowHandinRowOpen = false
  ) => {
    if (!submissions.length) {
      return (
        <Typography sx={styles.empty}>No students in this category</Typography>
      );
    }

    if (hideIdentities) {
      return (
        <Typography sx={styles.empty}>
          {submissions.length} students have not submitted
        </Typography>
      );
    }

    return useTablePresentation
      ? renderTable(submissions, allowHandinRowOpen)
      : renderCards(submissions, 'Submission date', allowHandinRowOpen);
  };

  const renderGroupHeader = (group: ResponsiveSubmissionGroup) => {
    return (
      <Box sx={styles.groupHeader}>
        <Typography sx={styles.groupName}>{group.name}</Typography>
        <Typography sx={styles.groupDetail}>
          {group.receivedAt ? formatDate(group.receivedAt) : 'No submission'}
        </Typography>
      </Box>
    );
  };

  const renderGroupAction = (group: ResponsiveSubmissionGroup) => {
    const firstSubmission = group.submissions[0];
    const canOpenGroup = Boolean(
      firstSubmission &&
        getItemDetails(firstSubmission).capabilities.canOpen &&
        onOpenSubmission
    );

    return (
      <Box sx={styles.groupActions}>
        {canOpenGroup && (
          <Button
            className="group-open-action"
            kind="secondary"
            onClick={(event) => {
              event.stopPropagation();
              openSubmission(firstSubmission);
            }}
            size="medium"
            sx={styles.groupOpenAction}
            type="button"
          >
            Open in {groupActionProviderTitle}
            <Icons.OpenInNew aria-hidden fontSize="small" />
          </Button>
        )}
      </Box>
    );
  };

  const renderGroupedSection = (key: SectionKey) => {
    const groups = groupedSubmissionGroups?.[key] || [];
    if (!groups.length) {
      return (
        <Typography sx={styles.empty}>No groups in this category</Typography>
      );
    }

    if (key === 'noSubmissions' && hideNoSubmissionIdentities) {
      return (
        <Typography sx={styles.empty}>
          {groups.length} groups have not submitted
        </Typography>
      );
    }

    if (useMobileGroupPresentation) {
      return (
        <Box sx={styles.nestedGroups}>
          {groups.map((group) => {
            const firstSubmission = group.submissions[0];
            const canOpenGroup = Boolean(
              firstSubmission &&
                getItemDetails(firstSubmission).capabilities.canOpen &&
                normalisedProvider === HANDIN_PROVIDER &&
                onOpenSubmission
            );

            return (
              <MobileGroupSubmission
                canOpenGroup={canOpenGroup}
                group={group}
                key={group.id || group.name}
                onOpenGroup={() =>
                  firstSubmission && openSubmission(firstSubmission)
                }
                styles={styles}
              >
                {renderCards(group.submissions, 'Submitted on', true)}
              </MobileGroupSubmission>
            );
          })}
        </Box>
      );
    }

    return (
      <Box sx={styles.nestedGroups}>
        {groups.map((group) => (
          <Accordion
            addOnEnd={renderGroupAction(group)}
            ariaLabel={`${group.name} (${group.submissions.length})`}
            arrowPosition="left"
            headerElement={renderGroupHeader(group)}
            key={group.id || group.name}
            size="small"
            sx={styles.nestedAccordion}
          >
            {renderSectionContent(group.submissions, false, true)}
          </Accordion>
        ))}
      </Box>
    );
  };

  const pendingDeleteName = submissionPendingDelete
    ? getSubmissionName(submissionPendingDelete)
    : '';

  return (
    <>
      <Box sx={styles.wrapper}>
        {sections.map(({ key, label }) => {
          const count = isGroupAssignment
            ? groupedSubmissionGroups?.[key]?.length || 0
            : submissionGroups[key].length;
          return (
            <Accordion
              ariaLabel={`${label} (${count})`}
              arrowPosition="left"
              defaultExpanded={
                expandPopulatedSections ? count > 0 : key === 'earlySubmissions'
              }
              key={`${key}-${expandPopulatedSections ? count : 'default'}`}
              label={`${label} (${count})`}
              size="medium"
              sx={styles.accordion}
            >
              {isGroupAssignment
                ? renderGroupedSection(key)
                : renderSectionContent(
                    submissionGroups[key],
                    key === 'noSubmissions' && hideNoSubmissionIdentities
                  )}
            </Accordion>
          );
        })}
        {isGroupAssignment && unassignedStudents.length > 0 && (
          <Accordion
            ariaLabel={`Not assigned to a group (${unassignedStudents.length})`}
            arrowPosition="left"
            defaultExpanded={expandPopulatedSections}
            label={`Not assigned to a group (${unassignedStudents.length})`}
            size="medium"
            sx={styles.accordion}
          >
            {hideNoSubmissionIdentities ? (
              <Typography sx={styles.empty}>
                {unassignedStudents.length} students are not assigned to a group
              </Typography>
            ) : (
              renderSectionContent(unassignedStudents)
            )}
          </Accordion>
        )}
      </Box>

      {submissionPendingDelete && (
        <DeleteSubmissionModal
          deleting={deleting}
          id={`delete-submission-${submissionPendingDelete.id}`}
          onClose={() => !deleting && setSubmissionPendingDelete(null)}
          onConfirm={confirmDesktopDelete}
          open
          studentName={pendingDeleteName}
        />
      )}
    </>
  );
};

export default SubmissionAccordions;
